import { useCallback, useMemo } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useMenuPermissions } from '../contexts/MenuPermissionsContext';
import { SellerTodayView } from '../components/seller/SellerOverviewView';
import { SellerListingsView } from '../components/seller/SellerListingsView';
import { SellerPromotionsView } from '../components/seller/SellerPromotionsView';
import { SellerAdsView } from '../components/seller/SellerAdsView';
import { SellerAgentView } from '../components/seller/SellerAgentView';
import { SellerSettingsDrawer, SETTINGS_TABS_BY_SECTION, SETTINGS_TAB_SECTION, normalizeSettingsTab } from '../components/seller/SellerSettingsView';
import { SellerSettingsContext } from '../components/seller/sellerUi';
import { useSellerAgentPending } from '../hooks/useSellerAgentPending';

// ---------------------------------------------------------------------------
// Seller em 4 telas, sem submenu:
//   Hoje       -> numeros da conta + o que fazer agora
//   Anuncios   -> metricas + saude (quadro) + impulsionar, do mesmo anuncio
//   Promocoes  -> tabela de precos + campanhas + historico
//   Ads        -> metricas + saude (quadro) das campanhas patrocinadas
// Configuracoes deixou de ser pagina: abre como painel lateral (?config=<aba>)
// na propria tela. As chaves de permissao antigas (seller.*) continuam valendo;
// uma tela aparece se o usuario tiver ao menos uma das chaves dela.
// ---------------------------------------------------------------------------

type SectionId = 'hoje' | 'anuncios' | 'promocoes' | 'ads' | 'agente';

interface Section {
  id: SectionId;
  label: string;
  hint: string;
  path: string;
  keys: string[];
  icon: JSX.Element;
}

const iconProps = { className: 'h-5 w-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', strokeWidth: 1.5, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

const SECTIONS: Section[] = [
  {
    id: 'hoje',
    label: 'Hoje',
    hint: 'Números e o que fazer agora',
    path: '/seller/hoje',
    keys: ['seller.overview'],
    icon: <svg {...iconProps}><path d="M3 12l9-8 9 8" /><path d="M5 10v10h14V10" /></svg>,
  },
  {
    id: 'anuncios',
    label: 'Anúncios',
    hint: 'Métricas, saúde e impulsionar',
    path: '/seller/anuncios',
    keys: ['seller.listings', 'seller.health', 'seller.boost'],
    icon: <svg {...iconProps}><path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" /></svg>,
  },
  {
    id: 'promocoes',
    label: 'Promoções',
    hint: 'Preços de campanha e adesões',
    path: '/seller/promocoes',
    keys: ['seller.promotions'],
    icon: <svg {...iconProps}><path d="M19 5L5 19" /><circle cx="7" cy="7" r="2.5" /><circle cx="17" cy="17" r="2.5" /></svg>,
  },
  {
    id: 'ads',
    label: 'Ads',
    hint: 'Campanhas patrocinadas e IA',
    path: '/seller/ads',
    keys: ['seller.ads-agent', 'seller.ads-health'],
    icon: <svg {...iconProps}><path d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>,
  },
  {
    id: 'agente',
    label: 'Agente',
    hint: 'Pergunte e peça mudanças',
    path: '/seller/agente',
    keys: ['seller.agente'],
    icon: <svg {...iconProps}><path d="M8 10h8M8 14h5" /><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12Z" /></svg>,
  },
];

// Links antigos (favoritos, notificacoes, toasts) continuam funcionando.
function legacyRedirect(pathname: string, search: URLSearchParams): string | null {
  if (pathname.startsWith('/seller/overview')) return '/seller/hoje';
  if (pathname.startsWith('/seller/listings')) return '/seller/anuncios';
  if (pathname.startsWith('/seller/health')) return '/seller/anuncios?view=quadro';
  if (pathname.startsWith('/seller/boost')) return '/seller/anuncios?impulsionar=1';
  if (pathname.startsWith('/seller/promotions/campaigns')) return '/seller/promocoes?aba=campanhas';
  if (pathname.startsWith('/seller/promotions')) return '/seller/promocoes';
  if (pathname.startsWith('/seller/ads-agent')) return '/seller/ads';
  if (pathname.startsWith('/seller/ads-health')) return '/seller/ads?view=quadro';
  if (pathname.startsWith('/seller/settings') || pathname.startsWith('/seller/analysis-config')) {
    const settingsTab = normalizeSettingsTab(search.get('tab')) || 'anuncios-rotinas';
    const section = SECTIONS.find((s) => s.id === SETTINGS_TAB_SECTION[settingsTab])!;
    return `${section.path}?config=${settingsTab}`;
  }
  return null;
}

function useVisibleSections() {
  const { canSee } = useMenuPermissions();
  return useMemo(() => SECTIONS.filter((s) => s.keys.some((k) => canSee(k))), [canSee]);
}

function isActive(pathname: string, section: Section) {
  return pathname === section.path || pathname.startsWith(section.path + '/');
}

function PendingBadge({ count }: { count: number }) {
  if (!count) return null;
  return <span className="ml-auto shrink-0 rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold leading-none text-amber-950" title={`${count} alteração(ões) esperando sua aprovação`}>{count}</span>;
}

function SellerSidebar({ sections, pending }: { sections: Section[]; pending: number }) {
  const location = useLocation();
  return (
    <aside className="fixed left-0 top-11 z-[1100] hidden h-[calc(100vh-2.75rem)] w-60 flex-col border-r border-orange-900/30 bg-[#7c2d12] md:flex">
      <div className="flex items-center gap-2.5 border-b border-orange-900/30 px-5 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-600/20">
          <svg className="h-5 w-5 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>
        <span className="text-sm font-bold text-white/90">Seller</span>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {sections.map((item) => {
            const active = isActive(location.pathname, item);
            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  className={`flex items-start gap-3 rounded-lg px-3 py-2.5 transition ${active ? 'bg-white/15 text-white shadow-sm' : 'text-white/65 hover:bg-white/10 hover:text-white/90'}`}
                >
                  <span className={`mt-0.5 ${active ? 'text-orange-300' : 'text-white/50'}`}>{item.icon}</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 text-sm font-medium">{item.label}{item.id === 'agente' && <PendingBadge count={pending} />}</span>
                    <span className={`block text-[11px] leading-tight ${active ? 'text-white/60' : 'text-white/35'}`}>{item.hint}</span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="border-t border-orange-900/30 px-5 py-3">
        <p className="text-[10px] text-white/30">Seller - AstraCampaign</p>
      </div>
    </aside>
  );
}

function SellerMobileNav({ sections, pending }: { sections: Section[]; pending: number }) {
  const location = useLocation();
  return (
    <div className="sticky top-11 z-[1000] overflow-hidden border-b border-gray-200 bg-white px-3 py-2 md:hidden">
      <nav className="flex max-w-full gap-2 overflow-x-auto">
        {sections.map((item) => {
          const active = isActive(location.pathname, item);
          return (
            <Link
              key={item.id}
              to={item.path}
              className={`flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${active ? 'bg-orange-50 text-orange-800' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <span className={active ? 'text-orange-700' : 'text-gray-400'}>{item.icon}</span>
              {item.label}
              {item.id === 'agente' && <PendingBadge count={pending} />}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

export function SellerPage() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { canSee } = useMenuPermissions();
  const sections = useVisibleSections();
  const fallbackPath = sections[0]?.path || '/';

  const current = sections.find((s) => isActive(location.pathname, s)) || null;
  const canConfigure = canSee('seller.analysis-config');
  const agentPending = useSellerAgentPending(canSee('seller.agente'));
  const configTab = normalizeSettingsTab(params.get('config'));
  const sectionTabs = current ? SETTINGS_TABS_BY_SECTION[current.id] : [];

  const openSettings = useCallback((tab?: string) => {
    const next = new URLSearchParams(params);
    next.set('config', normalizeSettingsTab(tab) || sectionTabs[0] || 'anuncios-rotinas');
    setParams(next);
  }, [params, setParams, sectionTabs]);

  const closeSettings = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete('config');
    setParams(next);
  }, [params, setParams]);

  const settingsCtx = useMemo(() => ({ openSettings, canConfigure }), [openSettings, canConfigure]);

  if (location.pathname === '/seller' || location.pathname === '/seller/') {
    return <Navigate to={fallbackPath} replace />;
  }
  const legacy = legacyRedirect(location.pathname, params);
  if (legacy) return <Navigate to={legacy} replace />;
  if (!current) return <Navigate to={fallbackPath} replace />;

  const can = (key: string) => canSee(key);

  return (
    <SellerSettingsContext.Provider value={settingsCtx}>
      <div className="min-h-screen">
        <SellerSidebar sections={sections} pending={agentPending.total} />
        <SellerMobileNav sections={sections} pending={agentPending.total} />
        <main className="p-4 md:ml-60 md:p-6">
          {current.id === 'hoje' && <SellerTodayView />}
          {current.id === 'anuncios' && (
            <SellerListingsView canTable={can('seller.listings')} canBoard={can('seller.health')} canBoost={can('seller.boost')} />
          )}
          {current.id === 'promocoes' && <SellerPromotionsView />}
          {current.id === 'ads' && <SellerAdsView canTable={can('seller.ads-agent')} canBoard={can('seller.ads-health')} />}
          {current.id === 'agente' && <SellerAgentView />}
        </main>
        {canConfigure && configTab && (
          <SellerSettingsDrawer
            tab={configTab}
            tabs={sectionTabs}
            onTab={(tab) => openSettings(tab)}
            onClose={closeSettings}
          />
        )}
      </div>
    </SellerSettingsContext.Provider>
  );
}
